import { AxiosError } from 'axios'
import dayjs from 'dayjs'
import { InboxSortDirection } from 'features/inbox/types/types'
import { getInspectionDate } from 'features/inbox/utils/date'
import {
  entryToIndication,
  entryToIndicationSet,
  IndicationSet,
  RolodexIndication,
} from 'features/indicationTriage'
import { RolodexEntryQueryBody, useRolodexEntries } from 'features/rolodex'
import { useMemo } from 'react'
import {
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentDataset,
  GeckoInspection,
  GeckoJob,
  GeckoMarkupLayer,
  GeckoUnit,
  RolodexEntryJson,
  RolodexEntryType,
} from 'types'

interface UseInboxRolodexEntriesParams {
  siteId?: string
  orgId?: string
  sortDirection?: InboxSortDirection
}

interface UseInboxRolodexEntriesResult {
  inspections: ReadonlyArray<GeckoInspection>
  jobs: ReadonlyArray<GeckoJob>
  componentDatasets: ReadonlyArray<GeckoComponentDataset>
  markupLayers: ReadonlyArray<GeckoMarkupLayer>
  units: ReadonlyArray<GeckoUnit>
  components: ReadonlyArray<GeckoComponent>
  assetModels: ReadonlyArray<GeckoAssetModel>
  indications: ReadonlyArray<RolodexIndication>
  indicationSets: ReadonlyArray<IndicationSet>
  isLoading: boolean
  error?: AxiosError
}

/**
 * Efficient hook that loads all inbox data with just 2 rolodex queries:
 * 1. Load inspections for the site/org
 * 2. Load all related data (jobs, CDSs, markup layers, units, components, asset models)
 *
 * This approach is inspired by useDetailsPageRolodexEntries and significantly
 * reduces the number of API calls and code complexity.
 */
export const useInboxRolodexEntries = ({
  siteId,
  orgId,
  sortDirection = 'newest',
}: UseInboxRolodexEntriesParams): UseInboxRolodexEntriesResult => {
  // Query 1: Load inspections for the site/org
  const inspectionQuery = useMemo(() => {
    const tags: Record<string, string> = {}

    if (siteId) {
      tags.site = siteId
    }
    if (orgId) {
      tags.organization = orgId
    }

    if (Object.keys(tags).length === 0) {
      return undefined
    }

    return {
      type: RolodexEntryType.INSPECTION,
      tags,
      order_by: [
        {
          key: 'date',
          descending: sortDirection === 'newest',
        },
      ],
      limit: 60000,
    }
  }, [siteId, orgId, sortDirection])

  const {
    entries: rawInspections,
    isLoading: isLoadingInspections,
    error: inspectionError,
  } = useRolodexEntries(inspectionQuery)

  const inspections = useMemo(() => {
    const fixedInspections = (rawInspections as GeckoInspection[]).map(
      (inspection) => ({
        ...inspection,
        data: {
          ...inspection.data,
          date: getInspectionDate(inspection),
        },
      })
    )

    // We sort the sorted data from Rolodex again, as the date inference may mean they are out of order
    return fixedInspections.sort((a, b) => {
      const dateA = dayjs(a.data.date)
      const dateB = dayjs(b.data.date)

      if (sortDirection === 'newest') {
        return dateB.diff(dateA)
      }
      return dateA.diff(dateB)
    })
  }, [rawInspections, sortDirection])

  // Query 2: Load all related data based on inspections
  // Extract unique IDs for jobs, components, and units from inspections
  const { jobIds, componentIds, unitIds, inspectionIds } = useMemo(() => {
    const { jobIdSet, componentIdSet, unitIdSet, inspectionIdList } =
      inspections.reduce(
        (acc, inspection) => {
          acc.inspectionIdList.push(inspection.id)

          if (inspection.tags?.job) {
            acc.jobIdSet.add(inspection.tags.job)
          }
          if (inspection.tags?.component) {
            acc.componentIdSet.add(inspection.tags.component)
          }
          if (inspection.tags?.unit) {
            acc.unitIdSet.add(inspection.tags.unit)
          }

          return acc
        },
        {
          jobIdSet: new Set<string>(),
          componentIdSet: new Set<string>(),
          unitIdSet: new Set<string>(),
          inspectionIdList: [] as string[],
        }
      )

    return {
      jobIds: Array.from(jobIdSet),
      componentIds: Array.from(componentIdSet),
      unitIds: Array.from(unitIdSet),
      inspectionIds: inspectionIdList,
    }
  }, [inspections])

  // Build queries for all related data types
  const relatedDataQueries = useMemo(() => {
    if (inspectionIds.length === 0) {
      return []
    }

    const queries: RolodexEntryQueryBody[] = []

    // Jobs - load by IDs extracted from inspections
    if (jobIds.length > 0) {
      queries.push({
        type: RolodexEntryType.JOB,
        ids: jobIds,
        order_by: [
          { key: 'start_date', descending: sortDirection === 'newest' },
        ],
        limit: 60000,
      })
    }

    // Components - load by IDs extracted from inspections
    if (componentIds.length > 0) {
      queries.push({
        type: RolodexEntryType.COMPONENT,
        ids: componentIds,
        limit: 10000,
      })
    }

    // Units - load by IDs extracted from inspections
    if (unitIds.length > 0) {
      queries.push({
        type: RolodexEntryType.UNIT,
        ids: unitIds,
        limit: 10000,
      })
    }

    // Asset Models - load by unit tags
    if (unitIds.length > 0) {
      const assetModelTags: Record<string, string[]> = { unit: unitIds }
      queries.push({
        type: RolodexEntryType.ASSET_MODEL,
        tags: assetModelTags,
        limit: 10000,
      })
    }

    // Component Datasets and Markup Layers - load by inspection tags
    // Combine these in a single query since they share the same filter
    const cdsAndMarkupTags: Record<string, string[]> = {
      inspection: inspectionIds,
    }
    queries.push({
      type: [RolodexEntryType.COMPONENT_DATASET, RolodexEntryType.MARKUP_LAYER],
      tags: cdsAndMarkupTags,
      limit: 60000,
    })

    // Indications - load by inspection tags
    queries.push({
      type: RolodexEntryType.INDICATION,
      tags: { inspection: inspectionIds },
      limit: 60000,
    })

    // Indication Sets - load for the site/org
    // We need to load all indication sets that might be relevant
    if (siteId || orgId) {
      const indicationSetTags: Record<string, string> = {}
      if (siteId) indicationSetTags.site = siteId
      if (orgId) indicationSetTags.organization = orgId
      queries.push({
        type: RolodexEntryType.INDICATION_SET,
        tags: indicationSetTags,
        limit: 10000,
      })
    }

    return queries
  }, [
    inspectionIds,
    jobIds,
    componentIds,
    unitIds,
    sortDirection,
    siteId,
    orgId,
  ])

  const {
    jobs,
    components,
    units,
    assetModels,
    componentDatasets,
    markupLayers,
    entries: relatedEntries,
    isLoading: isLoadingRelatedData,
    error: relatedDataError,
  } = useRolodexEntries(relatedDataQueries)

  const { indications, indicationSets } = useMemo(() => {
    if (!relatedEntries) {
      return { indications: [], indicationSets: [] }
    }

    const indicationEntries = relatedEntries
      .filter((entry) => entry.type === RolodexEntryType.INDICATION)
      .map((entry) => entryToIndication(entry as RolodexEntryJson))

    const indicationSetEntries = relatedEntries
      .filter((entry) => entry.type === RolodexEntryType.INDICATION_SET)
      .map((entry) => entryToIndicationSet(entry))

    return {
      indications: indicationEntries,
      indicationSets: indicationSetEntries,
    }
  }, [relatedEntries])

  const isLoading = isLoadingInspections || isLoadingRelatedData
  const error = inspectionError || relatedDataError

  return {
    inspections,
    jobs: (jobs ?? []) as GeckoJob[],
    componentDatasets: (componentDatasets ?? []) as GeckoComponentDataset[],
    markupLayers: (markupLayers ?? []) as GeckoMarkupLayer[],
    units: (units ?? []) as GeckoUnit[],
    components: (components ?? []) as GeckoComponent[],
    assetModels: (assetModels ?? []) as GeckoAssetModel[],
    indications,
    indicationSets,
    isLoading,
    error,
  }
}
